import {
  clamp,
  identity,
  mapAccum,
  memoizeWith,
  range,
  sortBy,
  uniq
} from 'ramda';
import { WATER_DENSITY, WATER_VISCOSITY_20C } from 'utils/backfill';
import { calculateFriction, homogeneousNewtonian } from '../../canvas/Model/updateFriction';
import { headMetresToKpa } from '../hydraulics';
import { crossSectionalArea } from '../Route/volumeUtils';
import { FLUSH_BISECTION_STEPS, FLUSH_SCAN_DIVISIONS, FLUSH_SCAN_MIN_STEP_METRES } from './flushConstants';
import { FlushFluid, FlushProfilePoint, PasteProperties } from './flushTypes';
import { VolumeTable } from './flushVolume';

// A route reduced to the plain numbers the pressure walk needs. Built once per solve from the model
// classes and never written back: PCPipe carries shared mutable friction/velocity state that the
// recipe HGL reads, and a sweep over hundreds of front positions must not touch it.
export interface FlushNode {
  id: string;
  distanceFromPump: number;
  elevation: number;
  isBooster: boolean;
  isChoke: boolean;
  chokeHead: number;
}

// segments[i] joins nodes[i] (stope side) to nodes[i + 1] (plant side), exactly like route spools.
export interface FlushSegment {
  insideDiameter: number;
  pressureRating: number;
  length: number;
}

export interface FlushLine {
  nodes: FlushNode[];
  segments: FlushSegment[];
  totalLength: number;
  table: VolumeTable;
}

// kPa/m for a fluid in a pipe at a flow rate. Memoised: the sweeps below evaluate the same pipe
// thousands of times and the gradient depends only on (fluid, diameter, flow rate).
export interface FrictionModel {
  gradient: (fluid: FlushFluid, insideDiameter: number, flowRate: number) => number;
}

const velocity = (flowRate: number, insideDiameter: number): number => {
  const area = crossSectionalArea(insideDiameter);
  return area > 0 ? (flowRate / area) / 3600 : 0;
};

export const createFrictionModel = (paste: PasteProperties): FrictionModel => {
  const compute = (fluid: FlushFluid, insideDiameter: number, flowRate: number): number => {
    const v = velocity(flowRate, insideDiameter);
    if (v <= 0 || insideDiameter <= 0) return 0;
    // Water goes straight to the Newtonian correlation. calculateFriction only reaches it when the
    // rheology's first yield-stress coefficient is zero, which a real paste rheology never is.
    const gradient = fluid === 'water'
      ? homogeneousNewtonian(insideDiameter, v, WATER_DENSITY, WATER_VISCOSITY_20C)
      : calculateFriction(
        paste.yieldStressA,
        paste.yieldStressB,
        paste.viscosityC,
        paste.viscosityD,
        paste.concentrationByMass,
        insideDiameter,
        v,
        paste.wetDensity,
        paste.frictionLossFactorOfSafety,
      );
    return Number.isFinite(gradient) ? gradient : 0;
  };
  return {
    gradient: memoizeWith((fluid, insideDiameter, flowRate) => `${fluid}|${insideDiameter}|${flowRate}`, compute),
  };
};

export interface PressureWalk {
  // Pressure at nodes[1..], i.e. pressures[i] belongs to nodes[i + 1]. nodes[0] (the discharge) is 0.
  pressures: number[];
  plantPressureKpa: number;
  // Any node whose pressure exceeds the rating of the pipe on its plant side.
  overPressure: boolean;
}

interface SegmentParts {
  waterKpa: number;
  pasteKpa: number;
}

// The pressure a segment adds walking stope -> plant, split by fluid. Water fills [0, front) metres
// from the plant and paste the rest, so a segment straddling the front is split, with its elevation
// change shared pro rata by length.
const segmentParts = (
  friction: FrictionModel,
  paste: PasteProperties,
  segment: FlushSegment,
  stopeNode: FlushNode,
  plantNode: FlushNode,
  front: number,
  flowRate: number,
): SegmentParts => {
  const { length, insideDiameter } = segment;
  if (length <= 0) return { waterKpa: 0, pasteKpa: 0 };
  const dz = stopeNode.elevation - plantNode.elevation;
  const waterLength = clamp(0, length, front - plantNode.distanceFromPump);
  const pasteLength = length - waterLength;
  const part = (fluid: FlushFluid, fluidLength: number, density: number): number => (fluidLength <= 0
    ? 0
    : headMetresToKpa(dz * (fluidLength / length), density) + (friction.gradient(fluid, insideDiameter, flowRate) * fluidLength));
  return { waterKpa: part('water', waterLength, WATER_DENSITY), pasteKpa: part('paste', pasteLength, paste.wetDensity) };
};

// The two-fluid version of PCRoute.updateRoutePointPressure. Walks stope -> plant accumulating the
// pressure the plant must supply, with the node rules preserved verbatim: a booster resets to 0, a
// choke adds its head (in the fluid present at that node), and every other node clamps at 0 (slack
// flow, accepted during a flush).
export const walkPressure = (
  line: FlushLine,
  friction: FrictionModel,
  paste: PasteProperties,
  front: number,
  flowRate: number,
): PressureWalk => {
  const { nodes, segments } = line;
  const densityAt = (distance: number): number => (distance < front ? WATER_DENSITY : paste.wetDensity);

  const step = (pressure: number, index: number): [number, number] => {
    const node = nodes[index];
    const { waterKpa, pasteKpa } = segmentParts(friction, paste, segments[index - 1], nodes[index - 1], node, front, flowRate);
    const accumulated = pressure + waterKpa + pasteKpa;
    const next = (() => {
      if (node.isBooster) return 0;
      if (node.isChoke) return Math.max(accumulated, 0) + headMetresToKpa(node.chokeHead, densityAt(node.distanceFromPump));
      return Math.max(accumulated, 0);
    })();
    const safe = Number.isFinite(next) ? next : 0;
    return [safe, safe];
  };

  const [plantPressureKpa, pressures] = mapAccum(step, 0, range(1, nodes.length));
  // Like colourRouteByPressure: both ends of a segment are held against that segment's rating.
  const nodePressure = (index: number): number => (index === 0 ? 0 : pressures[index - 1]);
  const overPressure = segments.some((segment, i) => Math.max(nodePressure(i), nodePressure(i + 1)) > segment.pressureRating);

  return { pressures, plantPressureKpa, overPressure };
};

export const requiredPressure = (
  line: FlushLine,
  friction: FrictionModel,
  paste: PasteProperties,
  front: number,
  flowRate: number,
): number => walkPressure(line, friction, paste, front, flowRate).plantPressureKpa;

export interface FrontState {
  pressureKpa: number;
  elevation: number;
}

// The pressure and elevation at the water front itself. The front generally lies inside a segment:
// its pressure is the stope-side node's pressure plus the paste-filled part of that segment (clamped
// at 0 like a node, so the drawn line never dips below the pipe), and its elevation is interpolated
// along the segment. At either end of the line it coincides with the end node.
export const frontState = (
  line: FlushLine,
  friction: FrictionModel,
  paste: PasteProperties,
  walk: PressureWalk,
  front: number,
  flowRate: number,
): FrontState => {
  const { nodes, segments } = line;
  const nodePressure = (index: number): number => (index === 0 ? 0 : walk.pressures[index - 1]);
  // segments[k] joins nodes[k] (stope side) to nodes[k + 1] (plant side).
  const k = segments.findIndex((_, i) => nodes[i + 1].distanceFromPump <= front && front < nodes[i].distanceFromPump);
  if (k < 0) {
    const atDischarge = front >= line.totalLength;
    const node = atDischarge ? nodes[0] : nodes[nodes.length - 1];
    return { pressureKpa: atDischarge ? 0 : walk.plantPressureKpa, elevation: node.elevation };
  }
  const stopeNode = nodes[k];
  const plantNode = nodes[k + 1];
  const { pasteKpa } = segmentParts(friction, paste, segments[k], stopeNode, plantNode, front, flowRate);
  const fraction = (front - plantNode.distanceFromPump) / (stopeNode.distanceFromPump - plantNode.distanceFromPump);
  return {
    pressureKpa: Math.max(nodePressure(k) + pasteKpa, 0),
    elevation: plantNode.elevation + ((stopeNode.elevation - plantNode.elevation) * fraction),
  };
};

export const toProfilePoints = (line: FlushLine, walk: PressureWalk, front: number): FlushProfilePoint[] => line.nodes
  .map((node, i) => ({
    nodeId: node.id,
    distanceFromPump: node.distanceFromPump,
    elevation: node.elevation,
    pressureKpa: i === 0 ? 0 : walk.pressures[i - 1],
    fluid: node.distanceFromPump < front ? 'water' : 'paste',
  }));

// Front positions to sample on [0, endDistance]: a uniform grid, every node inside the range, and
// both ends.
export const scanPositions = (line: FlushLine, endDistance: number): number[] => {
  if (endDistance <= 0) return [0];
  const stepSize = Math.max(endDistance / FLUSH_SCAN_DIVISIONS, FLUSH_SCAN_MIN_STEP_METRES);
  const grid = range(0, Math.floor(endDistance / stepSize) + 1).map((i) => i * stepSize);
  const nodeDistances = line.nodes
    .map((node) => node.distanceFromPump)
    .filter((distance) => distance > 0 && distance < endDistance);
  const rounded = [...grid, ...nodeDistances, endDistance].map((distance) => Number(distance.toFixed(6)));
  return uniq(sortBy(identity, rounded));
};

// The smallest front position from which the required plant pressure stays at or below `limitKpa`
// for every position up to `endDistance`. This is the pump (or air) handover point: the pressure
// profile is not monotonic in the front position, so a pump that took over at the first dip below
// the limit could stall at a later rise. The answer is the last crossing, found by a suffix maximum
// over the sampled profile and refined by bisection. Undefined when no such position exists.
export const handoverPoint = (
  line: FlushLine,
  friction: FrictionModel,
  paste: PasteProperties,
  limitKpa: number,
  flowRate: number,
  endDistance: number,
): number | undefined => {
  const pressureAt = (front: number): number => requiredPressure(line, friction, paste, front, flowRate);
  const positions = scanPositions(line, endDistance);
  const pressures = positions.map(pressureAt);
  const suffixMax = pressures.reduceRight<number[]>(
    (acc, pressure) => [Math.max(pressure, acc[0] ?? Number.NEGATIVE_INFINITY), ...acc],
    [],
  );
  const index = suffixMax.findIndex((maximum) => maximum <= limitKpa);
  if (index < 0) return undefined;
  if (index === 0) return positions[0];

  // positions[index - 1] fails and positions[index] passes; the crossing lies between them.
  const refined = range(0, FLUSH_BISECTION_STEPS).reduce(({ lo, hi }) => {
    const mid = (lo + hi) / 2;
    return pressureAt(mid) <= limitKpa ? { lo, hi: mid } : { lo: mid, hi };
  }, { lo: positions[index - 1], hi: positions[index] });
  return refined.hi;
};
